
import { softColors, getStatusChipStyles } from "./commonTableStyles";

const highlightColors = {
    start: '#fff7e6',
    middle: '#ffe0b2',
    end: '#fff7e6'
};

const styles = {
    container: {
        width: "100%"
    },

    headerBox: {
        p: 3,
        borderBottom: `1px solid ${softColors.border}`,
        display: "flex",
        flexDirection: "column",
        gap: 3,
        backgroundColor: '#fff',
        borderRadius: '12px 12px 0 0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
    },

    controlsBox: {
        display: 'flex',
        flexDirection: { xs: "column", md: "row" },
        gap: 2,
        alignItems: 'center'
    },

    statusSelect: (isDesktop) => ({
        width: isDesktop ? "200px" : "100%",
        minWidth: isDesktop ? "200px" : "100%",
        maxWidth: isDesktop ? "200px" : "100%",
        marginBottom: isDesktop ? 0 : "10px",
        padding: '10px 32px 10px 14px',
        fontSize: '0.95rem',
        fontWeight: 500,
        border: '1px solid #d1d5db',
        borderRadius: '12px',
        backgroundColor: '#fff',
        color: '#374151',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        outline: 'none',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        appearance: 'none',
        backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'left 10px center',
        backgroundSize: '16px',
        '&:hover': {
            borderColor: '#1976d2',
            boxShadow: '0 4px 16px rgba(25, 118, 210, 0.2)',
            transform: 'translateY(-2px)'
        },
        '&:focus': {
            borderColor: '#1976d2',
            boxShadow: '0 0 0 4px rgba(25, 118, 210, 0.15)',
            outline: 'none'
        }
    }),

    searchButton: (isDesktop) => ({
        width: isDesktop ? 'auto' : '100%'
    }),

    requiresAttentionRow: {
        position: 'relative',
        backgroundColor: 'rgba(255, 23, 68, 0.03) !important',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            backgroundColor: 'rgba(255, 23, 68, 0.06) !important',
            transform: 'scale(1.01)',
            boxShadow: '0 4px 12px rgba(255, 23, 68, 0.1)'
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '5px',
            background: 'linear-gradient(180deg, #ff1744 0%, #d32f2f 100%)',
            borderRadius: '0 4px 4px 0',
            animation: 'pulseGlow 2s ease-in-out infinite',
            pointerEvents: 'none',
            '@keyframes pulseGlow': {
                '0%': { opacity: 0.5, boxShadow: '0 0 10px rgba(255, 23, 68, 0.3)' },
                '50%': { opacity: 1, boxShadow: '0 0 20px rgba(255, 23, 68, 0.6)' },
                '100%': { opacity: 0.5, boxShadow: '0 0 10px rgba(255, 23, 68, 0.3)' }
            }
        }
    },

    overdueAttentionCard: {
        position: 'relative',
        overflow: 'visible',
        border: '2px solid #d32f2f !important',
        backgroundColor: 'rgba(255, 23, 68, 0.04) !important',
        boxShadow: '0 4px 20px rgba(211, 47, 47, 0.15) !important',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 32px rgba(211, 47, 47, 0.25) !important'
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            top: -4,
            right: -4,
            bottom: -4,
            left: -4,
            border: '3px solid rgba(211, 47, 47, 0.3)',
            borderRadius: 'inherit',
            animation: 'borderPulse 2.5s ease-in-out infinite',
            pointerEvents: 'none',
            '@keyframes borderPulse': {
                '0%': {
                    opacity: 0,
                    transform: 'scale(0.95)'
                },
                '50%': {
                    opacity: 1,
                    transform: 'scale(1)'
                },
                '100%': {
                    opacity: 0,
                    transform: 'scale(1.05)'
                }
            }
        }
    },

    pulse: {
        animation: 'pulse 2s infinite',
        '@keyframes pulse': {
            '0%': { opacity: 1, transform: 'scale(1)' },
            '50%': { opacity: 0.6, transform: 'scale(1.1)' },
            '100%': { opacity: 1, transform: 'scale(1)' }
        }
    },

    globalSearchInput: (isDesktop) => ({
        width: isDesktop ? "450px" : "100%",
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: '#ffffff',
            height: '42px',
            transition: 'all 0.3s ease',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
            '& fieldset': {
                borderColor: '#d1d5db'
            },
            '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transform: 'translateY(-1px)',
                '& fieldset': {
                    borderColor: '#9ca3af'
                }
            },
            '&.Mui-focused': {
                boxShadow: '0 4px 16px rgba(59, 130, 246, 0.25)',
                transform: 'translateY(-1px)',
                '& fieldset': {
                    borderColor: '#3b82f6',
                    borderWidth: '2px'
                }
            }
        }
    }),

    globalSearchClearButton: {
        p: 0.5,
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
            transform: 'rotate(90deg)'
        }
    },

    advancedSearchButton: (isDesktop) => ({
        width: isDesktop ? 'auto' : '100%',
        borderRadius: '8px',
        borderColor: '#d1d5db',
        color: '#374151',
        fontWeight: 500,
        px: 3,
        height: '42px',
        transition: 'all 0.3s ease',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
        '&:hover': {
            borderColor: '#9ca3af',
            backgroundColor: '#f9fafb',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            transform: 'translateY(-1px)'
        }
    }),

    globalSearchChip: {
        height: "32px",
        backgroundColor: '#e3f2fd',
        color: '#1565c0',
        borderRadius: '16px',
        fontWeight: 600,
        fontSize: '13px',
        border: '2px solid #90caf9',
        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: '#bbdefb',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.25)'
        },
        '& .MuiChip-deleteIcon': {
            color: '#1565c0',
            transition: 'all 0.2s ease',
            '&:hover': {
                color: '#0d47a1',
                transform: 'scale(1.2)'
            }
        }
    },

    advancedSearchChip: {
        height: "32px",
        backgroundColor: '#f5f5f5',
        color: '#666',
        borderRadius: '16px',
        fontWeight: 600,
        fontSize: '13px',
        border: '2px solid #e0e0e0',
        boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: '#eeeeee',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 10px rgba(0,0,0,0.12)'
        },
        '& .MuiChip-deleteIcon': {
            color: '#999',
            transition: 'all 0.2s ease',
            '&:hover': {
                color: '#666',
                transform: 'scale(1.2)'
            }
        }
    },

    uploadButtonStyled: (isDesktop) => ({
        fullWidth: !isDesktop,
        borderRadius: '8px',
        fontWeight: 500,
        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
        background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
            transition: 'left 0.5s ease'
        },
        '&:hover': {
            background: 'linear-gradient(135deg, #1565c0 0%, #0c3d8a 100%)',
            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
            transform: 'translateY(-2px)',
            '&::before': {
                left: '100%'
            }
        },
        '&:active': {
            transform: 'translateY(-1px)'
        }
    }),

    manualButtonStyled: (isDesktop) => ({
        fullWidth: !isDesktop,
        borderRadius: '8px',
        fontWeight: 500,
        backgroundColor: '#fff',
        color: '#374151',
        border: '1px solid #d1d5db',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            backgroundColor: '#f9fafb',
            borderColor: '#9ca3af',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
            transform: 'translateY(-1px)'
        }
    }),

    exportButton: {
        background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
        color: "#fff",
        borderRadius: '8px',
        fontWeight: 500,
        boxShadow: '0 2px 8px rgba(33, 115, 70, 0.2)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            background: 'linear-gradient(135deg, #1b5e20 0%, #0d3d10 100%)',
            boxShadow: '0 4px 12px rgba(33, 115, 70, 0.3)',
            transform: 'translateY(-2px)'
        },
        '&:active': {
            transform: 'translateY(-1px)'
        }
    },

    searchBarContainerStyled: {
        p: 3,
        backgroundColor: '#f8f9fa',
        borderRadius: '12px',
        mb: 2,
        boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.04)',
        animation: 'slideDown 0.3s ease-out',
        '@keyframes slideDown': {
            from: {
                opacity: 0,
                transform: 'translateY(-20px)'
            },
            to: {
                opacity: 1,
                transform: 'translateY(0)'
            }
        }
    },

    chipsBox: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1.5,
        animation: 'fadeIn 0.4s ease-out',
        '@keyframes fadeIn': {
            from: { opacity: 0 },
            to: { opacity: 1 }
        }
    },

    chipStyle: {
        height: "23px"
    },

    uploadButton: (isDesktop) => ({
        fullWidth: !isDesktop
    }),

    searchBarContainer: {
        p: 2,
        borderBottom: `1px solid ${softColors.border}`
    },

    tableContainer: {
        minHeight: '300px',
        borderRadius: '0 0 12px 12px',
        overflow: 'hidden'
    },

    dataGridContainer: {
        height: 400
    },

    mobileCard: {
        mb: 3,
        p: 3,
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            transform: 'translateY(-6px)',
            boxShadow: '0 12px 28px rgba(0,0,0,0.15)'
        }
    },

    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 3,
        pb: 2,
        borderBottom: '2px solid rgba(0, 0, 0, 0.06)'
    },

    cardGrid: {
        mb: 3,
        '& .MuiGrid-item': {
            mb: 2
        }
    },

    cardDivider: {
        my: 2.5
    },

    cardFooter: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mt: 2,
        pt: 1
    },

    cardActions: {
        display: 'flex',
        gap: 1
    },

    noInvoices: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        py: 6
    },

    approvalPackageButton: {
        minWidth: 'auto',
        p: 0
    },

    deleteButton: {
        color: 'grey.500'
    },

    mobileContent: {
        p: 2,
        minHeight: '200px',
        animation: 'fadeInUp 0.5s ease-out',
        '@keyframes fadeInUp': {
            from: {
                opacity: 0,
                transform: 'translateY(20px)'
            },
            to: {
                opacity: 1,
                transform: 'translateY(0)'
            }
        }
    },

    paginationContainer: {
        p: 2.5,
        borderTop: `2px solid ${softColors.border}`,
        backgroundColor: '#fafafa',
        borderRadius: '0 0 12px 12px',
        '& .MuiTablePagination-select': {
            borderRadius: '8px',
            transition: 'all 0.2s ease',
            '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)'
            }
        },
        '& .MuiIconButton-root': {
            transition: 'all 0.2s ease',
            '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                transform: 'scale(1.1)'
            }
        }
    },

    noResultsOverlay: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        color: 'text.secondary',
        fontSize: '1.1rem',
        py: 6
    },

    statusChip: (status) => ({
        ...getStatusChipStyles(status),
        height: '28px',
        fontSize: '0.8rem',
        fontWeight: 600,
        borderRadius: '14px',
        px: 1.5,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'all 0.2s ease',
        '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }
    }),

    editButton: {
        color: 'grey.500',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.12)',
            color: 'primary.main',
            transform: 'rotate(15deg) scale(1.1)'
        }
    },

    highlightAnimation: {
        '@keyframes highlight': {
            '0%': { 
                backgroundColor: highlightColors.start,
                boxShadow: '0 0 0 0 rgba(255, 224, 178, 0.7)'
            },
            '50%': { 
                backgroundColor: highlightColors.middle,
                boxShadow: '0 0 20px 10px rgba(255, 224, 178, 0)'
            },
            '100%': { 
                backgroundColor: highlightColors.end,
                boxShadow: '0 0 0 0 rgba(255, 224, 178, 0)'
            }
        },
        animation: 'highlight 3s ease-in-out'
    },

    actionsMenuButton: {
        color: 'grey.600',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.12)',
            color: 'primary.main',
            transform: 'rotate(90deg) scale(1.15)'
        }
    },

    actionsMenu: {
        borderRadius: '12px',
        minWidth: 240,
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        border: '1px solid rgba(0,0,0,0.05)',
        backdropFilter: 'blur(10px)',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        '& .MuiMenuItem-root': {
            padding: '14px 18px',
            gap: '14px',
            borderRadius: '8px',
            margin: '4px 8px',
            transition: 'all 0.2s ease',
            '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                transform: 'translateX(-4px)',
                paddingRight: '22px'
            },
            '&.Mui-disabled': {
                opacity: 0.4
            }
        },
        '& .MuiDivider-root': {
            margin: '10px 0'
        }
    },

    menuItemText: {
        fontSize: '14px',
        fontWeight: 600
    },

    menuItemIcon: {
        fontSize: '20px',
        transition: 'all 0.2s ease'
    },

    deleteMenuItem: {
        color: 'error.main',
        '&:hover': {
            backgroundColor: 'rgba(211, 47, 47, 0.08)'
        }
    }
};

export { highlightColors };

export default styles;